import React, { useState } from "react";
import { Helmet } from "react-helmet";
import "../css/whois.css";

import {
  FaGlobe,
  FaSearch,
  FaCalendarAlt,
  FaUser,
  FaServer,
  FaCopy,
  FaCheck,
  FaSpinner,
  FaExclamationTriangle,
  FaShieldAlt,
  FaRegistered,
} from "react-icons/fa";

// Fetch JSON via XMLHttpRequest so browser-extension overrides of window.fetch
// cannot interfere. Returns parsed JSON on success, null on any error.
function safeFetch(url, opts = {}) {
  return new Promise((resolve) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.timeout = opts.timeout ?? 5000;
      xhr.open("GET", url);
      if (opts.headers) {
        Object.entries(opts.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      }
      xhr.onload = () => {
        try {
          resolve(xhr.status >= 200 && xhr.status < 300 ? JSON.parse(xhr.responseText) : null);
        } catch {
          resolve(null);
        }
      };
      xhr.onerror   = () => resolve(null);
      xhr.ontimeout = () => resolve(null);
      xhr.send();
    } catch {
      resolve(null);
    }
  });
}

// rdap.org is an ICANN-operated public RDAP aggregator that:
//  - supports every TLD without needing per-TLD URLs
//  - sends Access-Control-Allow-Origin: * (no CORS issues from the browser)
// It is used as the primary source. No fallback map needed.
const RDAP_ORG = "https://rdap.org/domain/";

const Whois = () => {
  const [domain, setDomain] = useState("");
  const [whoisData, setWhoisData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState("");

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!domain.trim()) {
      setError("Please enter a domain name");
      return;
    }

    const cleanDomain = domain
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .replace(/\/.*$/, "");

    if (!/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9-]{2,})+$/.test(cleanDomain)) {
      setError("Please enter a valid domain name (e.g., example.com)");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setWhoisData(null);

      // ── Step 1: parallel DNS queries ──────────────────────────────────────
      const [soaRes, nsRes, aRes] = await Promise.all([
        safeFetch(`https://dns.google/resolve?name=${cleanDomain}&type=SOA`),
        safeFetch(`https://dns.google/resolve?name=${cleanDomain}&type=NS`),
        safeFetch(`https://dns.google/resolve?name=${cleanDomain}&type=A`),
      ]);

      if (soaRes?.Status !== 0 && nsRes?.Status !== 0) {
        throw new Error(`"${cleanDomain}" does not appear to exist. Please check the domain name and try again.`);
      }

      const nameServers = nsRes?.Answer?.map(r => r.data.replace(/\.$/, "")) ?? [];
      const ipAddresses = aRes?.Answer?.map(r => r.data) ?? [];

      const whoisInfo = {
        domain: cleanDomain,
        registrar: null,
        registrarIANAId: null,
        registrationDate: null,
        expirationDate: null,
        lastUpdated: null,
        status: [],
        nameServers,
        ipAddresses,
        registrant: null,
      };

      // ── Step 2: RDAP lookup via rdap.org (CORS-safe for all TLDs) ─────────
      const rd = await safeFetch(`${RDAP_ORG}${cleanDomain}`, {
        headers: { Accept: "application/rdap+json, application/json" },
        timeout: 8000,
      });

      if (rd) {
        if (rd?.objectClassName === "domain") {
          const events = rd.events ?? [];
          whoisInfo.registrationDate = events.find(ev => ev.eventAction === "registration")?.eventDate ?? null;
          whoisInfo.expirationDate   = events.find(ev => ev.eventAction === "expiration")?.eventDate ?? null;
          whoisInfo.lastUpdated      = events.find(ev => ev.eventAction === "last changed")?.eventDate ?? null;

          if (rd.status?.length) whoisInfo.status = rd.status;

          if (rd.nameservers?.length) {
            whoisInfo.nameServers = rd.nameservers
              .map(ns => (ns.ldhName || ns.unicodeName || "").toLowerCase())
              .filter(Boolean);
          }

          const parseVcard = (entity) => {
            const fields = entity?.vcardArray?.[1] ?? [];
            const get = (key) => fields.find(f => f[0] === key)?.[3] ?? null;
            const adr = get("adr");
            const org = get("org");
            return {
              name:         get("fn"),
              organization: typeof org === "object" ? null : org,
              email:        get("email"),
              phone:        get("tel"),
              city:         Array.isArray(adr) ? (adr[3] || null) : null,
              state:        Array.isArray(adr) ? (adr[4] || null) : null,
              country:      Array.isArray(adr) ? (adr[6] || null) : null,
            };
          };

          const entities = rd.entities ?? [];
          const registrarEnt  = entities.find(e => e.roles?.includes("registrar"));
          const registrantEnt = entities.find(e => e.roles?.includes("registrant"));

          if (registrarEnt) {
            const fn = registrarEnt.vcardArray?.[1]?.find(f => f[0] === "fn");
            whoisInfo.registrar = fn?.[3] ?? registrarEnt.fn ?? null;
            const ianaId = registrarEnt.publicIds?.find(id => id.type === "IANA Registrar ID");
            if (ianaId) whoisInfo.registrarIANAId = ianaId.identifier;
          }

          if (registrantEnt) {
            whoisInfo.registrant = parseVcard(registrantEnt);
          }
        }
      }

      if (!whoisInfo.status.length) whoisInfo.status = ["active"];

      setWhoisData(whoisInfo);

    } catch (err) {
      console.error("WHOIS lookup error:", err);
      setError(err.message || `Unable to fetch WHOIS information. The domain may not exist or its data may be private.`);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text, field) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(field);
      setTimeout(() => setCopied(""), 2000);
    }).catch(() => {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(field);
      setTimeout(() => setCopied(""), 2000);
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getDaysUntilExpiry = (expirationDate) => {
    if (!expirationDate) return null;
    try {
      return Math.ceil((new Date(expirationDate) - new Date()) / 86400000);
    } catch {
      return null;
    }
  };

  // Reusable row: shows value or "Private / Not available" placeholder
  const InfoRow = ({ label, value, copyKey }) => (
    <div className="info-row-minimal">
      <span className="info-label">{label}</span>
      <div className="info-value-group">
        {value ? (
          <>
            <span className="info-value">{value}</span>
            {copyKey && (
              <button
                onClick={() => copyToClipboard(value, copyKey)}
                className="copy-btn-minimal"
              >
                {copied === copyKey ? <FaCheck /> : <FaCopy />}
              </button>
            )}
          </>
        ) : (
          <span className="info-value unavailable">Private / Not available</span>
        )}
      </div>
    </div>
  );

  return (
    <div className="whois-page">
      <Helmet>
        <title>WHOIS Lookup – Domain Registration Details | ClubHosty</title>
        <meta
          name="description"
          content="Free WHOIS lookup tool to check domain registration details, expiration dates, registrar information, and contact details for any domain name."
        />
        <meta
          name="keywords"
          content="whois lookup, domain lookup, domain registration, domain expiry, registrar, domain owner"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="whois-hero-minimal">
        <div className="container">
          <div className="hero-content-minimal">
            <div className="hero-text-section">
              <div
                className="hero-badge"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  background:
                    "linear-gradient(135deg, rgba(33,188,195,0.12) 0%, rgba(59,138,242,0.12) 100%)",
                  color: "#21bcc3",
                  padding: "6px 16px",
                  borderRadius: "20px",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  marginBottom: "20px",
                }}
              >
                <FaGlobe style={{ fontSize: "0.8rem" }} /> Domain Intelligence
                Tool
              </div>
              <h1 className="hero-title-minimal">WHOIS Lookup</h1>
              <p className="hero-desc-minimal">
                Get complete domain registration and ownership information
              </p>
            </div>

            <div className="whois-search-minimal">
              <form onSubmit={handleSearch} className="search-form-minimal">
                <div className="search-input-minimal">
                  <FaGlobe className="input-icon-minimal" />
                  <input
                    type="text"
                    placeholder="Enter domain name (e.g., example.com)"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    className="domain-input-minimal"
                  />
                  <button
                    type="submit"
                    className="search-btn-minimal"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <FaSpinner className="spinner" />
                    ) : (
                      <FaSearch />
                    )}
                    {isLoading ? "Looking up..." : "Lookup"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Error Section */}
      {error && (
        <section className="error-section-minimal">
          <div className="container">
            <div className="error-card-minimal">
              <div className="error-content-minimal">
                <FaExclamationTriangle className="error-icon-minimal" />
                <p>{error}</p>
                <button
                  onClick={() => setError(null)}
                  className="retry-btn-minimal"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Results Section */}
      {whoisData && (
        <section className="whois-results-minimal">
          <div className="container">
            <div className="results-header-minimal">
              <h2>
                WHOIS Information for{" "}
                <span className="domain-name-minimal">{whoisData.domain}</span>
              </h2>
            </div>

            <div className="whois-info-grid-minimal">

              {/* Domain Dates */}
              <div className="whois-card-minimal">
                <div className="card-header-minimal">
                  <div className="card-icon-wrapper">
                    <FaCalendarAlt className="card-icon-minimal" />
                  </div>
                  <h3>Domain Dates</h3>
                </div>
                <div className="card-content-minimal">
                  <InfoRow
                    label="Registered"
                    value={formatDate(whoisData.registrationDate)}
                    copyKey="registration"
                  />

                  {/* Expiration (with days-remaining badge) */}
                  <div className="info-row-minimal">
                    <span className="info-label">Expiration</span>
                    <div className="info-value-group">
                      {whoisData.expirationDate ? (
                        <>
                          <span className="info-value">
                            {formatDate(whoisData.expirationDate)}
                            {getDaysUntilExpiry(whoisData.expirationDate) !== null && (
                              <span
                                className={`expiry-badge ${
                                  getDaysUntilExpiry(whoisData.expirationDate) < 30
                                    ? "expiry-warning"
                                    : "expiry-normal"
                                }`}
                              >
                                {getDaysUntilExpiry(whoisData.expirationDate)} days
                              </span>
                            )}
                          </span>
                          <button
                            onClick={() =>
                              copyToClipboard(whoisData.expirationDate, "expiration")
                            }
                            className="copy-btn-minimal"
                          >
                            {copied === "expiration" ? <FaCheck /> : <FaCopy />}
                          </button>
                        </>
                      ) : (
                        <span className="info-value unavailable">
                          Private / Not available
                        </span>
                      )}
                    </div>
                  </div>

                  <InfoRow
                    label="Last Updated"
                    value={formatDate(whoisData.lastUpdated)}
                    copyKey="updated"
                  />
                </div>
              </div>

              {/* Registrar */}
              <div className="whois-card-minimal">
                <div className="card-header-minimal">
                  <div className="card-icon-wrapper">
                    <FaRegistered className="card-icon-minimal" />
                  </div>
                  <h3>Registrar</h3>
                </div>
                <div className="card-content-minimal">
                  <InfoRow
                    label="Registrar"
                    value={whoisData.registrar}
                    copyKey="registrar"
                  />
                  {whoisData.registrarIANAId && (
                    <InfoRow label="IANA ID" value={whoisData.registrarIANAId} />
                  )}
                  {whoisData.status.length > 0 && (
                    <div className="info-row-minimal">
                      <span className="info-label">Status</span>
                      <div className="status-list">
                        {whoisData.status.slice(0, 3).map((s, i) => (
                          <span key={i} className="status-badge">
                            {s}
                          </span>
                        ))}
                        {whoisData.status.length > 3 && (
                          <span className="status-more">
                            +{whoisData.status.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Name Servers */}
              {whoisData.nameServers.length > 0 && (
                <div className="whois-card-minimal">
                  <div className="card-header-minimal">
                    <div className="card-icon-wrapper">
                      <FaServer className="card-icon-minimal" />
                    </div>
                    <h3>Name Servers</h3>
                  </div>
                  <div className="card-content-minimal">
                    {whoisData.nameServers.map((ns, i) => (
                      <InfoRow
                        key={i}
                        label={`NS ${i + 1}`}
                        value={ns}
                        copyKey={`ns-${i}`}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* IP Addresses */}
              {whoisData.ipAddresses.length > 0 && (
                <div className="whois-card-minimal">
                  <div className="card-header-minimal">
                    <div className="card-icon-wrapper">
                      <FaGlobe className="card-icon-minimal" />
                    </div>
                    <h3>IP Addresses</h3>
                  </div>
                  <div className="card-content-minimal">
                    {whoisData.ipAddresses.map((ip, i) => (
                      <InfoRow
                        key={i}
                        label={`IP ${i + 1}`}
                        value={ip}
                        copyKey={`ip-${i}`}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Registrant Contact */}
              <div className="whois-card-minimal">
                <div className="card-header-minimal">
                  <div className="card-icon-wrapper">
                    <FaUser className="card-icon-minimal" />
                  </div>
                  <h3>Registrant Contact</h3>
                </div>
                <div className="card-content-minimal">
                  <InfoRow
                    label="Name"
                    value={whoisData.registrant?.name}
                    copyKey="reg-name"
                  />
                  <InfoRow
                    label="Organization"
                    value={whoisData.registrant?.organization}
                    copyKey="reg-org"
                  />
                  <InfoRow
                    label="Email"
                    value={whoisData.registrant?.email}
                    copyKey="reg-email"
                  />
                  <InfoRow
                    label="Phone"
                    value={whoisData.registrant?.phone}
                    copyKey="reg-phone"
                  />
                  {(whoisData.registrant?.city ||
                    whoisData.registrant?.state ||
                    whoisData.registrant?.country) && (
                    <InfoRow
                      label="Location"
                      value={[
                        whoisData.registrant.city,
                        whoisData.registrant.state,
                        whoisData.registrant.country,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    />
                  )}
                  <div className="privacy-notice">
                    <FaShieldAlt className="privacy-icon" />
                    <span>
                      Most domains use privacy protection to hide contact details
                    </span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>
      )}

      {/* Understanding WHOIS */}
      <section className="whois-info-minimal">
        <div className="container">
          <div className="info-content-minimal">
            <h2>Understanding WHOIS</h2>
            <div className="info-grid-minimal">
              <div className="info-card-minimal">
                <div className="info-icon-wrapper">
                  <FaCalendarAlt className="info-icon-minimal" />
                </div>
                <h4>Registration Dates</h4>
                <p>Track when domain was registered and expires</p>
              </div>
              <div className="info-card-minimal">
                <div className="info-icon-wrapper">
                  <FaUser className="info-icon-minimal" />
                </div>
                <h4>Contact Info</h4>
                <p>Find registrant and administrative contacts</p>
              </div>
              <div className="info-card-minimal">
                <div className="info-icon-wrapper">
                  <FaServer className="info-icon-minimal" />
                </div>
                <h4>Name Servers</h4>
                <p>View authoritative DNS servers for the domain</p>
              </div>
              <div className="info-card-minimal">
                <div className="info-icon-wrapper">
                  <FaShieldAlt className="info-icon-minimal" />
                </div>
                <h4>Privacy Protection</h4>
                <p>Many domains use privacy services to hide details</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {copied && (
        <div className="copy-notification">
          <FaCheck className="notification-icon" />
          <span>Copied!</span>
        </div>
      )}
    </div>
  );
};

export default Whois;
